import { Box, Flex, HStack, Text } from '@chakra-ui/react';
import {
  StudentEmployerPlacementStatus,
  StudentWorkPlacementJourneyStatus,
} from '@duke-hq/svc-api-db';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { t } from '@lingui/core/macro';
import { Fragment } from 'react';
import { type ChangeableStage, type JourneyStageKey, journeyStages } from './journeyStages';
import { testIds } from './testIds';

function stageIndex(key: JourneyStageKey): number {
  return journeyStages().findIndex((stage) => stage.key === key);
}

function placedStageIndex(employerPlacementStatus: StudentEmployerPlacementStatus | null): number {
  switch (employerPlacementStatus) {
    case StudentEmployerPlacementStatus.AWAITING_EVIDENCE:
      return stageIndex('evidence');
    case StudentEmployerPlacementStatus.COMPLETED:
    case StudentEmployerPlacementStatus.FAILED:
    case StudentEmployerPlacementStatus.CANCELLED:
      return stageIndex('outcome');
    default:
      return stageIndex('confirmed');
  }
}

function getCurrentStageIndex(
  journeyStatus: StudentWorkPlacementJourneyStatus | null,
  employerPlacementStatus: StudentEmployerPlacementStatus | null,
): number {
  switch (journeyStatus) {
    case StudentWorkPlacementJourneyStatus.AWAITING_CHECKLIST:
      return stageIndex('documents');
    case StudentWorkPlacementJourneyStatus.AWAITING_APPROVAL:
      return stageIndex('ready');
    case StudentWorkPlacementJourneyStatus.AWAITING_PLACEMENT:
      return stageIndex('sourcing');
    case StudentWorkPlacementJourneyStatus.INTERVIEWING:
      return stageIndex('interview');
    case StudentWorkPlacementJourneyStatus.AWAITING_CONTRACT:
      return stageIndex('contracts');
    case StudentWorkPlacementJourneyStatus.PLACED:
      return placedStageIndex(employerPlacementStatus);
    case StudentWorkPlacementJourneyStatus.COMPLETED:
      return stageIndex('outcome');
    default:
      return 0;
  }
}

export function PlacementStepper({
  journeyStatus,
  employerPlacementStatus,
  onHold = false,
  onSelectStage,
}: {
  journeyStatus: StudentWorkPlacementJourneyStatus | null;
  employerPlacementStatus: StudentEmployerPlacementStatus | null;
  onHold?: boolean;
  onSelectStage?: (stage: ChangeableStage) => void;
}): React.ReactElement {
  const stages = journeyStages();
  const currentIndex = getCurrentStageIndex(journeyStatus, employerPlacementStatus);
  const outcomeIndex = stages.length - 1;

  return (
    <Flex
      align="center"
      gap={0}
      w="full"
      justifyContent="space-between"
      opacity={onHold ? 0.6 : 1}
      aria-disabled={onHold}
      data-testid={testIds.placementStepper}
    >
      {stages.map((stage, index) => {
        const isComplete = index < currentIndex;
        const isCurrent = index === currentIndex;
        const isLast = index === stages.length - 1;

        let label = stage.label;
        let bg = isComplete ? 'bg.accent.inverse-default' : 'transparent';
        let borderColor = isComplete || isCurrent ? 'border.accent' : 'border.default';
        let color = isComplete ? 'fg.inverse' : isCurrent ? 'fg.accent.emphasis' : 'fg.muted';

        if (index === outcomeIndex && isCurrent) {
          if (employerPlacementStatus === StudentEmployerPlacementStatus.FAILED) {
            label = t`Failed`;
            bg = 'danger';
            borderColor = 'danger';
            color = 'fg.inverse';
          } else if (employerPlacementStatus === StudentEmployerPlacementStatus.CANCELLED) {
            label = t`Cancelled`;
            bg = 'warning';
            borderColor = 'warning';
            color = 'fg.inverse';
          } else {
            bg = 'success';
            borderColor = 'success';
            color = 'fg.inverse';
          }
        }

        if (onHold) {
          const isFilled = isComplete || (index === outcomeIndex && isCurrent);
          bg = isFilled ? 'fg.muted' : 'transparent';
          borderColor = isFilled || isCurrent ? 'fg.muted' : 'border.default';
          color = isFilled ? 'fg.inverse' : isCurrent ? 'fg.default' : 'fg.muted';
        }

        let connectorBg = 'border.default';
        if (isComplete) {
          connectorBg = onHold ? 'fg.muted' : 'bg.accent.inverse-default';
        }

        const targetStage = stage.journeyStatus;
        const isSelectable =
          onSelectStage != null && !onHold && targetStage != null && targetStage !== journeyStatus;

        return (
          <Fragment key={stage.key}>
            <HStack
              aria-current={isCurrent ? 'step' : undefined}
              minW={pxToRem(100)}
              justify="center"
              gap={1.5}
              px={3}
              py={1}
              borderRadius="full"
              borderWidth="2px"
              borderColor={borderColor}
              bg={bg}
              color={color}
              fontWeight={isCurrent || isComplete ? 'semibold' : 'medium'}
              fontSize="sm"
              {...(isSelectable && targetStage != null
                ? {
                    as: 'button',
                    type: 'button',
                    cursor: 'pointer',
                    onClick: () => onSelectStage(targetStage),
                    'aria-label': t`Change stage to ${stage.label}`,
                    'data-testid': testIds.stepperStageButton,
                    _hover: { borderColor: 'border.accent', color: 'fg.accent.emphasis' },
                    _focusVisible: {
                      outline: `${pxToRem(2)} solid`,
                      outlineColor: 'border.accent',
                    },
                  }
                : {})}
            >
              <Text as="span" fontSize="sm" color={color}>
                {label}
              </Text>
            </HStack>
            {!isLast ? (
              <Box flex={1} w={pxToRem(20)} height={pxToRem(2)} bg={connectorBg} flexShrink={0} />
            ) : null}
          </Fragment>
        );
      })}
    </Flex>
  );
}
